/**
 * Classroom calibration profiles.
 *
 * A calibration profile describes the *style* of problem currently being
 * taught in class, independent of what a generator is theoretically capable
 * of. Generators consult the profile to decide how often a feature appears
 * (frequency), whether a feature is allowed at all (a "never" frequency or an
 * explicit flag), and the numeric ranges / operation depth expected.
 *
 * The profile intentionally OVERRIDES difficulty breadth: if fractional
 * solutions have not been introduced in class, Standard difficulty will not
 * emit them even though the generator supports them. This is the mechanism
 * that keeps generated practice aligned to the actual classroom sequence, and
 * it is what gets adjusted as real worksheets/quizzes/tests become available.
 */

import type { TopicId } from "./curriculum";

/** How often a feature should appear. Maps to a relative weight. */
export type Frequency = "never" | "rare" | "sometimes" | "frequent" | "always";

export const FREQUENCY_WEIGHT: Record<Frequency, number> = {
  never: 0,
  rare: 1,
  sometimes: 3,
  frequent: 7,
  always: 100,
};

export function frequencyWeight(f: Frequency): number {
  return FREQUENCY_WEIGHT[f];
}

/**
 * Decide a yes/no feature from a frequency, deterministically via the caller's
 * RNG probability. "always"/"never" are hard; the middle values are odds.
 */
export function frequencyProbability(f: Frequency): number {
  switch (f) {
    case "never":
      return 0;
    case "rare":
      return 0.15;
    case "sometimes":
      return 0.4;
    case "frequent":
      return 0.75;
    case "always":
      return 1;
  }
}

/**
 * Feature frequencies shared across equation-style topics. Not every topic
 * uses every field; generators read the ones relevant to them. Fields are
 * optional so a partial profile can override just what changed, merged over a
 * topic's defaults.
 */
export interface CalibrationFeatures {
  readonly distribution?: Frequency;
  readonly variablesBothSides?: Frequency;
  readonly negativeCoefficients?: Frequency;
  readonly combiningLikeTerms?: Frequency;
  readonly multipleParentheses?: Frequency;
  /** How often the one-solution value is a non-integer fraction. */
  readonly fractionalSolutions?: Frequency;
  /** How often fractional coefficients/constants appear. */
  readonly fractionalCoefficients?: Frequency;
  /** Special solution categories - "never" until introduced in class. */
  readonly noSolutionCases?: Frequency;
  readonly infiniteSolutionCases?: Frequency;
}

/** Inclusive range for the number of solving steps ("operation depth"). */
export interface DepthRange {
  readonly min: number;
  readonly max: number;
}

export interface CalibrationProfile {
  readonly topic: TopicId;
  /** Human note describing what this profile represents. */
  readonly note?: string;
  readonly features: CalibrationFeatures;
  /** Typical operation depth taught for this topic. */
  readonly typicalDepth?: DepthRange;
  /**
   * Hard cap on absolute integer coefficient magnitude, overriding difficulty
   * defaults when the classroom uses smaller/larger numbers.
   */
  readonly maxCoefficientMagnitude?: number;
}

/**
 * Default profiles. These encode the initial best guess for each topic's
 * classroom style and are refined as real material arrives. Only topics with a
 * meaningful profile are listed; generators fall back to their own difficulty
 * defaults for anything absent.
 *
 * Variables on Both Sides is calibrated to the provided classroom assessment:
 * frequent distribution, both-sides, negatives, and combining like terms;
 * integer solutions the norm; fractional solutions rare; special cases (no
 * solution / infinite) NOT yet introduced; depth 4-6 steps.
 */
export const DEFAULT_CALIBRATION: Partial<Record<TopicId, CalibrationProfile>> =
  {
    "variables-both-sides": {
      topic: "variables-both-sides",
      note:
        "Calibrated to Big Ideas Math 1.3, which teaches 'Identifying Special " +
        "Solutions' (identity / no solution) alongside one-solution equations. " +
        "Distribute-heavy, integer solutions the norm, special cases present " +
        "but not dominant.",
      features: {
        distribution: "frequent",
        variablesBothSides: "frequent",
        negativeCoefficients: "frequent",
        combiningLikeTerms: "frequent",
        multipleParentheses: "sometimes",
        fractionalSolutions: "rare",
        fractionalCoefficients: "never",
        // Section 1.3 explicitly teaches identity and no-solution cases, so
        // they now appear at a low frequency rather than being suppressed.
        noSolutionCases: "sometimes",
        infiniteSolutionCases: "sometimes",
      },
      typicalDepth: { min: 4, max: 6 },
      maxCoefficientMagnitude: 12,
    },
    "multi-step-equations": {
      topic: "multi-step-equations",
      note: "Distribute + combine like terms, integer solutions common.",
      features: {
        distribution: "frequent",
        negativeCoefficients: "frequent",
        combiningLikeTerms: "frequent",
        fractionalSolutions: "rare",
        fractionalCoefficients: "never",
      },
      typicalDepth: { min: 3, max: 5 },
      maxCoefficientMagnitude: 12,
    },
  };

/** Merge an override profile's features over a base, returning a full profile. */
export function mergeCalibration(
  base: CalibrationProfile,
  override: Partial<CalibrationProfile>,
): CalibrationProfile {
  return {
    ...base,
    ...override,
    features: { ...base.features, ...(override.features ?? {}) },
    ...(override.typicalDepth
      ? { typicalDepth: override.typicalDepth }
      : base.typicalDepth
        ? { typicalDepth: base.typicalDepth }
        : {}),
  };
}

/** Resolve a feature frequency, applying a sensible fallback when unset. */
export function featureFrequency(
  profile: CalibrationProfile | undefined,
  feature: keyof CalibrationFeatures,
  fallback: Frequency,
): Frequency {
  const f = profile?.features[feature];
  return f ?? fallback;
}

export function getDefaultCalibration(
  topic: TopicId,
): CalibrationProfile | undefined {
  return DEFAULT_CALIBRATION[topic];
}
